import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { utilities } from '~/common';

const { isSSR } = utilities;

type Props = {
  placeholder?: React.ReactNode;
};

const LazyRenderContainer: React.FC<PropsWithChildren<Props>> = ({
  children,
  placeholder = null,
}) => {
  const [isLoadedOnDemand, setIsLoadedOnDemand] = useState(false);

  const onVisible = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      // Loop through entires to see if one is visible
      const isVisible = entries.some(entry => {
        const { isIntersecting } = entry;
        return isIntersecting;
      });
      if (isVisible && !isLoadedOnDemand && observerRef.current !== null) {
        observerRef.current.disconnect();
        setIsLoadedOnDemand(true);
      }
    },
    [isLoadedOnDemand],
  );
  const [wrapper, setWrapper] = useState<null | HTMLDivElement>(null);

  const observerRef = useRef(
    new IntersectionObserver(onVisible, {
      // When 'root' set to null, the viewport is used
      root: null,
      rootMargin: '0px',
      threshold: 0,
    }),
  );

  useEffect(() => {
    const { current: observer } = observerRef;
    if (wrapper !== null) {
      observer.observe(wrapper);
    }
    return () => {
      if (wrapper !== null) {
        observer.disconnect();
      }
    };
  }, [wrapper]);

  return isLoadedOnDemand ? (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  ) : (
    <div ref={setWrapper}>{placeholder}</div>
  );
};

/**
 * if you use LazyRender with SSR, provide the corresponding 'placeholder' to prevent grows of Cumulative Layout Shift (CLS)
 * https://web.dev/cls/
 */
export const LazyRender: React.FC<PropsWithChildren<Props>> = props => {
  const { placeholder } = props;
  if (isSSR()) {
    return placeholder ? <div>{placeholder}</div> : null;
  }
  return <LazyRenderContainer {...props} />;
};
